import { useState, useEffect } from "react";
import { Star } from "lucide-react";
import { Routes, Route, useParams } from "react-router-dom";
import { supabase } from "./supabaseClient";
import "./App.css";

// Formulaire pour un établissement spécifique
function ReviewForm() {
  const { shortId } = useParams();
  const [hovered, setHovered] = useState(0);
  const [phase, setPhase] = useState("loading"); // "loading" | "error" | "stars" | "form" | "sending" | "success"
  const [rating, setRating] = useState(0);
  const [establishment, setEstablishment] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // Champs du formulaire natif
  const [customValues, setCustomValues] = useState({});

  useEffect(() => {
    async function fetchEstablishment() {
      if (!shortId) {
        setErrorMsg("Lien invalide.");
        setPhase("error");
        return;
      }

      try {
        const { data, error } = await supabase
          .from("establishments")
          .select("*")
          .eq("short_id", shortId)
          .single();

        if (error || !data) {
          console.error("Establishment not found");
          setErrorMsg("Établissement introuvable ou problème de connexion.");
          setPhase("error");
        } else {
          setEstablishment(data);
          document.title = `Avis client - ${data.name}`;
          
          // Synchronisation du thème sur l'élément <html> (pour outrepasser le système)
          if (data.theme === 'dark') {
            document.documentElement.classList.add('dark');
            document.documentElement.classList.remove('light');
          } else {
            document.documentElement.classList.remove('dark');
            document.documentElement.classList.add('light');
          }
          
          const storedReview = localStorage.getItem(`has_reviewed_${shortId}`);
          let alreadyReviewed = false;
          
          if (storedReview) {
            try {
              const { expiry } = JSON.parse(storedReview);
              if (Date.now() < expiry) {
                alreadyReviewed = true;
              } else {
                localStorage.removeItem(`has_reviewed_${shortId}`);
              }
            } catch (e) {
              // Fallback pour les anciens cookies 'true'
              alreadyReviewed = true;
            }
          }

          if (alreadyReviewed) {
            setPhase("already_reviewed");
          } else {
            setPhase("stars");
          }

          // Chargement dynamique des polices
          const titleFont = data.font_title || 'Inter';
          const bodyFont = data.font_body || 'Inter';
          const fontsToLoad = Array.from(new Set([titleFont, bodyFont])).map(f => `family=${f.replace(/ /g, '+')}`).join('&');
          
          if (fontsToLoad) {
            const linkId = 'google-fonts-link';
            let link = document.getElementById(linkId);
            if (!link) {
              link = document.createElement('link');
              link.id = linkId;
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
            link.href = `https://fonts.googleapis.com/css2?${fontsToLoad}&display=swap`;
          }
        }
      } catch (err) {
        console.error("Fetch error");
        setErrorMsg("Une erreur inattendue est survenue.");
        setPhase("error");
      }
    }

    fetchEstablishment();
  }, [shortId]);

  const handleStarClick = async (r) => {
    if (r >= 4) {
      if (establishment.google_review_url) {
        setPhase("redirecting"); // Affiche un loader plein écran le temps de la requête
        
        try {
          const webhookUrl = process.env.REACT_APP_WEBHOOK_URL || 'https://review-manager-dashboard.vercel.app/api/incoming-feedback';
          await fetch(webhookUrl, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${establishment.api_key}`
            },
            body: JSON.stringify({
              rating: Number(r),
              comment: "Redirigé vers Google" // Ou simplement "" selon ce que ton API préfère
            })
          });
        } catch (error) {
          console.error("Impossible de logguer l'avis positif :", error);
        }
        
        // Marquer comme déjà répondu (Expire dans 30 jours / 1 mois)
        const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000;
        localStorage.setItem(`has_reviewed_${shortId}`, JSON.stringify({ expiry }));
        
        // Redirection après l'envoi
        window.location.href = establishment.google_review_url;
      } else {
        setRating(r);
        setPhase("form");
      }
      return;
    }
    setRating(r);
    setPhase("form");
  };

  // Check that all required fields are filled
  const isFormValid = () => {
    const fields = establishment?.custom_fields || [];
    // Must have at least one field with a value
    const hasAnyValue = fields.some(f => {
      const val = customValues[f.id];
      if (Array.isArray(val)) return val.length > 0;
      return val && String(val).trim().length > 0;
    });
    if (!hasAnyValue) return false;

    // All required fields must be filled
    return fields.filter(f => f.required).every(f => {
      const val = customValues[f.id];
      if (f.type === 'chips') return Array.isArray(val) && val.length > 0;
      if (f.type === 'select' || f.type === 'radio' || f.type === 'yes_no') return !!val;
      return val && String(val).trim().length > 0;
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    setPhase("sending");

    try {
      const webhookUrl = process.env.REACT_APP_WEBHOOK_URL || 'https://review-manager-dashboard.vercel.app/api/incoming-feedback';
      
      // Map field IDs to their values. Special handling for 'comment' and 'email' keys.
      const payload = {
        rating: Number(rating),
        ...customValues,
      };

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${establishment.api_key}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Erreur serveur');
      }
      
      const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000;
      localStorage.setItem(`has_reviewed_${shortId}`, JSON.stringify({ expiry }));
      setPhase("success");
    } catch (error) {
      console.error("Review submission failed");
      alert("Une erreur est survenue lors de l'envoi de votre avis.");
      setPhase("form");
    }
  };

  if (phase === "loading") {
    return (
      <div className="review-container review-container--centered">
        <div className="review-spinner-small" style={{width: 50, height: 50, borderTopColor: 'currentColor'}} />
        <p style={{marginTop: '1rem'}}>Chargement...</p>
      </div>
    );
  }

  if (phase === "redirecting") {
    return (
      <div className="review-container review-container--centered">
        <div className="review-spinner-small" style={{width: 50, height: 50, borderTopColor: 'currentColor'}} />
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="review-container review-container--centered" style={{textAlign: 'center'}}>
        <h2>{errorMsg}</h2>
        <p style={{marginTop: '1rem', opacity: 0.7}}>Veuillez vérifier votre lien ou l'existence de l'établissement.</p>
      </div>
    );
  }



  const containerStyle = {
    "--primary": establishment?.primary_color || "#f49432",
    "--primary-glow": `${establishment?.primary_color || "#f49432"}8C`,
    "--font-title": establishment?.font_title ? `'${establishment.font_title}', sans-serif` : '"Inter", sans-serif',
    "--font-body": establishment?.font_body ? `'${establishment.font_body}', sans-serif` : '"Inter", sans-serif'
  };

  return (
    <div className={`review-container ${["stars", "already_reviewed"].includes(phase) ? "review-container--centered" : "review-container--top"}`} style={containerStyle}>

      {/* ── HEADER COMMUN (Logo ou Texte) ── */}
      {(phase !== "stars" && phase !== "already_reviewed") && (
        <div className="review-header animate-fadeup" style={{ flexShrink: 0 }}>
          {establishment?.display_preference === "logo" && establishment?.logo_url ? (
            <img src={establishment.logo_url} alt={establishment.display_name || establishment.name} className="review-logo-sm" data-testid="review-logo" />
          ) : (
            <h1 className="review-header-text-sm" style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>
              {establishment?.display_name || establishment?.name || "Votre Établissement"}
            </h1>
          )}
        </div>
      )}

      {/* ── PHASE 1 : ETOILES & DEJA AVISÉ ── */}
      {(phase === "stars" || phase === "already_reviewed") && (
        <div className="review-group animate-fadeup">
          {establishment?.display_preference === "logo" && establishment?.logo_url ? (
            <img src={establishment.logo_url} alt={establishment.display_name || establishment.name} className="review-logo" data-testid="review-logo" />
          ) : (
            <h1 className="review-header-text" style={{ fontSize: '2.5rem', fontWeight: 700, margin: '0 0 1rem 0' }}>
              {establishment?.display_name || establishment?.name || "Votre Établissement"}
            </h1>
          )}
          
          {phase === "stars" && (
            <>
              <p className="review-subtitle" data-testid="subtitle" style={{marginTop: '1rem'}}>
                {establishment?.text_stars || `Comment s'est passée votre expérience chez ${establishment?.name || ''} ?`}
              </p>
              <div className="review-stars" data-testid="star-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    data-testid={`star-${star}`}
                    className="review-star-btn"
                    onClick={() => handleStarClick(star)}
                    onMouseEnter={() => setHovered(star)}
                    onMouseLeave={() => setHovered(0)}
                    onTouchStart={() => setHovered(star)}
                    onTouchEnd={() => setHovered(0)}
                  >
                    <Star
                      className={`review-star ${star <= hovered ? "review-star--active" : "review-star--inactive"}`}
                    />
                  </button>
                ))}
              </div>
            </>
          )}

          {phase === "already_reviewed" && (
             <p className="review-subtitle" style={{marginTop: '1.5rem', fontSize: '1.1rem', maxWidth: '320px', lineHeight: 1.5}}>
               {establishment?.text_already_reviewed || "Vous avez déjà donné votre avis auparavant, merci à vous."}
             </p>
          )}
        </div>
      )}

      {/* ── PHASE 2 : FORMULAIRE NATIF ── */}
      {(phase === "form" || phase === "sending") && (
        <>
          <p className="review-apology animate-fadeup" data-testid="apology-message">
            {establishment?.text_form ? (
              <span style={{ whiteSpace: "pre-wrap" }}>{establishment.text_form}</span>
            ) : (
              <>Désolé que votre expérience n'ait pas été parfaite chez {establishment?.name || ''},<br />
              aidez-nous à nous améliorer.</>
            )}
          </p>

          <form className="review-native-form" onSubmit={handleFormSubmit}>

            {/* All fields rendered dynamically from custom_fields */}
            {(establishment?.custom_fields || []).sort((a,b) => (a.order||0) - (b.order||0)).map((field) => {
              const val = customValues[field.id] ?? '';
              const disabled = phase === "sending";
              const labelSuffix = field.required ? '' : ' (facultatif)';

              if (field.type === 'text' || field.type === 'email') {
                return (
                  <input
                    key={field.id}
                    type={field.type}
                    className="review-input"
                    placeholder={`${field.label}${labelSuffix}`}
                    value={val}
                    onChange={(e) => setCustomValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                    disabled={disabled}
                  />
                );
              }

              if (field.type === 'phone') {
                return (
                  <input
                    key={field.id}
                    type="tel"
                    inputMode="numeric"
                    className="review-input"
                    placeholder={`${field.label}${labelSuffix}`}
                    value={val}
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/[^0-9+\s\-()]/g, '');
                      setCustomValues(prev => ({ ...prev, [field.id]: cleaned }));
                    }}
                    disabled={disabled}
                  />
                );
              }

              if (field.type === 'textarea') {
                return (
                  <textarea
                    key={field.id}
                    className="review-textarea"
                    placeholder={`${field.label}${labelSuffix}`}
                    value={val}
                    onChange={(e) => setCustomValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                    disabled={disabled}
                    rows={3}
                  />
                );
              }

              if (field.type === 'select') {
                return (
                  <div key={field.id} className="review-field-group">
                    <label className="review-field-label">{field.label}{labelSuffix}</label>
                    <select
                      className="review-select"
                      value={val}
                      onChange={(e) => setCustomValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                      disabled={disabled}
                    >
                      <option value="">Choisir...</option>
                      {(field.options || []).map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                );
              }

              if (field.type === 'chips') {
                const selected = Array.isArray(val) ? val : [];
                return (
                  <div key={field.id} className="review-field-group">
                    <label className="review-field-label">{field.label}{labelSuffix}</label>
                    <div className="review-chips">
                      {(field.options || []).map(opt => (
                        <button
                          key={opt}
                          type="button"
                          disabled={disabled}
                          className={`review-chip ${selected.includes(opt) ? 'review-chip--active' : ''}`}
                          onClick={() => {
                            const next = selected.includes(opt)
                              ? selected.filter(v => v !== opt)
                              : [...selected, opt];
                            setCustomValues(prev => ({ ...prev, [field.id]: next }));
                          }}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }

              if (field.type === 'radio') {
                return (
                  <div key={field.id} className="review-field-group">
                    <label className="review-field-label">{field.label}{labelSuffix}</label>
                    <div className="review-radios">
                      {(field.options || []).map(opt => (
                        <button
                          key={opt}
                          type="button"
                          disabled={disabled}
                          className={`review-radio-btn ${val === opt ? 'review-radio-btn--active' : ''}`}
                          onClick={() => setCustomValues(prev => ({ ...prev, [field.id]: opt }))}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }

              if (field.type === 'yes_no') {
                return (
                  <div key={field.id} className="review-field-group">
                    <label className="review-field-label">{field.label}{labelSuffix}</label>
                    <div className="review-yesno">
                      {['Oui', 'Non'].map(opt => (
                        <button
                          key={opt}
                          type="button"
                          disabled={disabled}
                          className={`review-yesno-btn ${val === opt ? 'review-yesno-btn--active' : ''}`}
                          onClick={() => setCustomValues(prev => ({ ...prev, [field.id]: opt }))}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }

              return null;
            })}
            
            <button 
              type="submit" 
              className="review-submit-btn" 
              disabled={phase === "sending" || !isFormValid()}
            >
              {phase === "sending" ? (
                <>
                  <div className="review-spinner-small" /> Envoi en cours...
                </>
              ) : (
                "Envoyer mon avis"
              )}
            </button>
          </form>

          {establishment?.google_review_url && (
            <a 
              href={establishment.google_review_url}
              className="review-google-link animate-fadeup"
            >
              Laisser mon avis sur Google
            </a>
          )}
        </>
      )}

      {/* ── PHASE 3 : SUCCÈS ── */}
      {phase === "success" && (
        <div className="review-success-msg">
          <p>{establishment?.text_success || "Merci pour vos retours. Nous prendrons les mesures nécessaires."}</p>
        </div>
      )}

    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/:shortId" element={<ReviewForm />} />
      <Route path="/" element={
        <div className="review-container review-container--centered">
          <h2>Bienvenue sur notre plateforme d'avis</h2>
          <p>Veuillez scanner le QR Code de votre établissement pour laisser un avis.</p>
        </div>
      } />
    </Routes>
  );
}
